import { OPERATION_OPTIONS } from "constants/unit";
import { isNil } from "lodash";
import { formatDate } from "utils/dayjs";

export const GRID_PHASE_OPTIONS = [
  { value: 1, label: "Single Phase" },
  { value: 3, label: "3-Phase" },
];

export const getLabel = (options = [], value) => {
  const item = options?.find((it) => it.value === value);
  return item?.label || "";
};

export const getStateName = (siteDetails, location) => {
  if (siteDetails?.id && location.length > 0) {
    const item = location.find((it) => it.location === siteDetails?.location);
    return item?.state_name;
  }
};

export const getSiteAddressSummary = (siteDetails) => {
  return `${siteDetails?.street}, ${siteDetails?.city}, ${siteDetails?.state}, ${siteDetails?.postcode}`;
};

export const getCommissioningDatesSummary = (details) => {
  return `Installing: ${formatlifecycleDate(
    details?.installing,
  )}, Installed: ${formatlifecycleDate(
    details?.installed,
  )}, Commissioned: ${formatlifecycleDate(
    details?.commissioned,
  )}, Operational: ${formatlifecycleDate(details?.operational)}`;
};

export const getDnspandPhaseSummary = (dnspOptions, siteDetails) => {
  return `${getLabel(dnspOptions, siteDetails?.dnsp)}, ${getLabel(
    GRID_PHASE_OPTIONS,
    siteDetails?.phases,
  )}`;
};

export const getBackupConnectionSummary = (unitDetails) => {
  return unitDetails?.backup
    ? `Enabled, ${unitDetails?.setting?.reserved ?? 0}% reserved capacity`
    : "Disabled";
};

export const LED_BRIGHTNESS_MIN = 1;
export const LED_BRIGHTNESS_MAX = 31;
export const LED_BRIGHTNESS_DEFAULT = 8;
// Default night off window: 10pm (off) to 8am (on)
export const LED_NIGHT_DEFAULT = { start: 22, end: 8 };

// Format an hour (0-24) into a friendly time label e.g. 22 -> "10pm", 8 -> "8am"
export const formatLedHour = (hour) => {
  const h = ((Number(hour) % 24) + 24) % 24;
  if (h === 0) return "12am";
  if (h === 12) return "12pm";
  return h < 12 ? `${h}am` : `${h - 12}pm`;
};

export const getLedLocationSummary = (ledcontrol) => {
  const hasIndoor =
    ledcontrol?.indoor !== undefined && ledcontrol?.indoor !== null;
  return hasIndoor
    ? `Indoors  Brightness: ${ledcontrol.indoor} (of maximum ${LED_BRIGHTNESS_MAX})`
    : "Outdoors";
};

export const getLedNightSummary = (ledcontrol) => {
  const night = ledcontrol?.night;
  if (!night || night.start === undefined || night.start === null) {
    return "disabled";
  }
  return `LED OFF between ${formatLedHour(night.start)} and ${formatLedHour(
    night.end,
  )}`;
};

export const getRegionSummary = (region, siteDetails) => {
  return getLabel(region, siteDetails?.region);
};

export const getExportLimitSummary = (unitDetails) => {
  return !isNil(unitDetails?.as4777?.export_limit)
    ? `Enabled, ${unitDetails?.as4777?.export_limit * 10}W`
    : "Disabled";
};

export const powerRateLimitSummary = (unitDetails) =>
  unitDetails?.as4777?.wgra ? `${unitDetails?.as4777?.wgra}%/min` : "NA";

export const getRemoteControlSummary = (unitDetails) => {
  return unitDetails?.drm0
    ? "DRM0 connected / enabled"
    : "DRM0 not connected / disabled";
};

export const getVoltWattSummary = (unitDetails) => {
  return `Volt-Watt ${unitDetails?.as4777?.voltwatt ? "enabled" : "disabled"}`;
};

export const getVoltVarSummary = (unitDetails) => {
  return `Volt-Var ${unitDetails?.as4777?.voltvar ? "enabled" : "disabled"}`;
};

export const getPowerFactorSummary = (unitDetails) => {
  return `Power Factor ${unitDetails?.as4777?.power_factor || ""} 
    (${unitDetails?.as4777?.power_factor > 0 ? "Supplying" : "Absorbing"})`;
};

export const getPvSummary = (unitDetails) => {
  if (!unitDetails?.pv) return "-";
  const pv = Object.keys(unitDetails?.pv).filter(
    (it) => !isNil(unitDetails?.pv[it]),
  );
  let external = pv.includes("pvext") ? "+ External PV" : "";
  let internalpv = pv.filter((p) => p !== "pvext").length;
  let internal = `Internal PV - ${internalpv} Channel${
    internalpv !== 1 ? "s" : ""
  }`;

  return `${internal} ${external}`;
};

export const joinBy = (list = [], key = "name", separator = ", ") => {
  const value = list.map((it) => it[key]).join(separator);

  return <span style={{ marginRight: "4px" }}>{value ? value : "-"}</span>;
};

const formatlifecycleDate = (date) => {
  return date ? formatDate(new Date(date * 1000), "DD/MM/YYYY") : "-";
};

const METER_BASE = [
  { label: "Current", key: "current" },
  { label: "Voltage", key: "voltage" },
  { label: "Power Factor", key: "power_factor" },
  { label: "Active Power", key: "power_active" },
  { label: "Reactive Power", key: "power_reactive" },
  { label: "Apparent Power", key: "power_apparent" },
  { label: "Frequency", key: "frequency" },
];

export const isPrimitive = (v) =>
  ["string", "number", "boolean"].includes(typeof v);

const getMeterData = (property, matchingKeys, data, isTotal = false) => {
  const key = matchingKeys.find((k) =>
    isTotal ? k === property : k.includes(property),
  );
  const val =
    key && data[key] !== undefined && data[key] !== null ? data[key] : "";
  if (val === "") return "";
  if (typeof val === "number" && Number.isFinite(val)) {
    return Number.isInteger(val) ? String(val) : val.toFixed(2);
  }
  return String(val);
};

export const constructMeterBucket = (meterData) => {
  return Object.entries(meterData)
    .filter(([key]) => key !== "expiry")
    .map(([label, meterObj]) => {
      const keys = Object.keys(meterObj);

      const list = METER_BASE.map((item, id) => {
        const matchingKeys = keys.filter((k) => k.includes(item.key));

        return {
          id,
          label: item.label,
          phase1: getMeterData("phase1", matchingKeys, meterObj),
          phase2: getMeterData("phase2", matchingKeys, meterObj),
          phase3: getMeterData("phase3", matchingKeys, meterObj),
          total: getMeterData(item.key, matchingKeys, meterObj, true),
        };
      });

      return { label, list };
    });
};

export const constructDebugBucket = (debug) => {
  const allowed = [
    "events",
    "fpga",
    "meter",
    "bms",
    "network",
    "battery",
    "site",
  ];

  const buckets = {};

  for (const node of allowed) {
    if (!debug[node]) continue;

    if (node === "meter") {
      buckets[node] = constructMeterBucket(debug[node]);
      continue;
    }

    buckets[node] = buildNodeBucket(node, debug[node]);
  }

  return buckets;
};

const buildNodeBucket = (node, nodeData) => {
  const result = [];
  const details = { title: "Details", value: null, nodes: [] };

  for (const key of Object.keys(nodeData)) {
    if (node === "battery" && key === "expiry") continue;

    const value = nodeData[key];

    if (Array.isArray(value)) {
      if (key.startsWith("bms_board")) {
        result.push(buildBmsBoardBucket(key, value));
      }
      continue;
    }

    if (isPrimitive(value)) {
      details.nodes.push({
        title: "",
        childrens: [{ label: key, value }],
      });
      continue;
    }

    if (typeof value === "object" && value !== null) {
      const objBucket = buildObjectBucket(key, value);
      if (objBucket) result.push(objBucket);
    }
  }

  result.push(details);
  return result;
};

const buildBmsBoardBucket = (title, array) => {
  return {
    title,
    value: null,
    nodes: array.map((item, index) => ({
      title: `${title} ${index + 1}`,
      childrens: Object.keys(item).map((k) => ({
        label: k,
        value: item[k],
      })),
    })),
  };
};

const buildObjectBucket = (title, obj) => {
  const bucket = { title, value: null, nodes: [] };

  for (const key of Object.keys(obj)) {
    const value = obj[key];
    const child = { title: "", childrens: [] };

    if (typeof value === "object" && value !== null) {
      child.title = key;
      child.childrens = Object.entries(value)
        // eslint-disable-next-line no-unused-vars
        .filter(([_, v]) => isPrimitive(v))
        .map(([label, val]) => ({ label, value: val }));
    } else if (isPrimitive(value)) {
      child.childrens.push({ label: key, value });
    }

    if (child.childrens.length > 0) {
      bucket.nodes.push(child);
    }
  }

  if (bucket.nodes.length === 0) return null;
  return bucket;
};

export const getOperationMode = (data) => {
  const mode = OPERATION_OPTIONS.MODE.find((it) => {
    const val = data[it.value];
    return (
      val ||
      val === 0 ||
      (["hotwater", "pv_curtail", "pvext_curtail"].includes(it.value) &&
        val !== undefined)
    );
  });

  return mode || { value: "", label: "" };
};

export const formatPriceExport = (value) => (value ? value * -1.0 : value);

const isNestedSection = (data) =>
  Object.values(data).every(
    (value) => typeof value === "object" && value !== null && !Array.isArray(value),
  );

const buildVersionSection = (title, data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return null;
  }

  const entries = Object.entries(data);
  if (entries.length === 0) {
    return null;
  }

  if (isNestedSection(data)) {
    return {
      title,
      twoLevel: true,
      nodes: entries.map(([key, value]) => ({
        title: key,
        childrens: Object.entries(value).map(([label, val]) => ({
          label,
          value: val,
        })),
      })),
    };
  }

  return {
    title,
    twoLevel: false,
    nodes: [
      {
        title: "",
        childrens: entries.map(([label, value]) => ({ label, value })),
      },
    ],
  };
};

export const normalizeVersionSections = (data) => {
  if (!data) return [];

  if (Array.isArray(data)) {
    return data
      .map((item) =>
        item?.nodes ? item : buildVersionSection(item?.title, item),
      )
      .filter(Boolean);
  }

  if (typeof data === "object") {
    return Object.entries(data)
      .map(([title, value]) => {
        if (value?.nodes) {
          return { title, ...value };
        }
        return buildVersionSection(title, value);
      })
      .filter(Boolean);
  }

  return [];
};
